import React, { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { luxColors, Icon, Button, Iffy, Stack, util, DNABox, DNAText, DNAButton, Popover, } from '@alucio/lux-ui';
import { useLocalVideoToggle, useLocalAudioToggle, LocalAudioLevelIndicator, } from '@alucio/video';
import { DeviceSelect, AudioTestButton, Status } from '../../../DeviceSettings/DeviceSettings';
import LocalVideoPreview from '../../../LocalVideoPreview/LocalVideoPreview';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
const styles = StyleSheet.create({
    audioContainer: {
        backgroundColor: luxColors.warning.senary,
        borderColor: luxColors.warning.quaternary,
        borderRadius: 4,
        borderWidth: 1,
        padding: 20,
    },
    audioSubtitle: {
        fontFamily: 'Lato',
        fontSize: 13,
        fontWeight: 'bold',
        paddingBottom: 4,
    },
    button: {
        backgroundColor: luxColors.opacityBackground.darker,
        borderRadius: 25,
        borderWidth: 0,
        height: 40,
        marginLeft: 8,
        width: 40,
    },
    buttonInverted: {
        backgroundColor: '#3C3C3CFF',
    },
    buttonNextSelector: {
        marginLeft: -15,
    },
    icon: {
        color: luxColors.confirmationBackground.primary,
        height: 22,
        width: 22,
    },
    localVideoPreview: {
        backgroundColor: luxColors.basicBlack.primary,
        minHeight: 160,
    },
    mainAudioBoxTitle: {
        color: luxColors.warning.quaternary,
        fontSize: 16,
        fontWeight: '700',
        paddingBottom: 20,
    },
    previewStatusContainer: {
        backgroundColor: luxColors.opacityBackground.darker,
        borderRadius: 2,
        height: 24,
        marginTop: 6,
        paddingBottom: 0,
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 3,
        position: 'absolute',
        zIndex: 1,
    },
    previewText: {
        color: colors['color-text-basic'],
        fontSize: 13,
        fontStyle: 'normal',
        opacity: 1.0,
        textAlign: 'center',
    },
});
var MediaAccessState;
(function (MediaAccessState) {
    MediaAccessState[MediaAccessState["unknown"] = 0] = "unknown";
    MediaAccessState[MediaAccessState["prompted"] = 1] = "prompted";
    MediaAccessState[MediaAccessState["allowed"] = 2] = "allowed";
    MediaAccessState[MediaAccessState["denied"] = 3] = "denied";
    MediaAccessState[MediaAccessState["notAvailable"] = 4] = "notAvailable";
    MediaAccessState[MediaAccessState["notFound"] = 5] = "notFound";
})(MediaAccessState || (MediaAccessState = {}));
function Preview(props) {
    const { onSettingsChanged, isPhoneAudio, isAudioPreview, audioFiles, audioAccessState, videoAccessState } = props;
    const [audioLayout, setAudioLayout] = useState(0);
    const [isVideoEnabled, toggleVideoEnabled] = useLocalVideoToggle();
    const [isAudioEnabled, toggleAudioEnabled] = useLocalAudioToggle();
    const audioException = (audioAccessState !== MediaAccessState.allowed);
    const videoException = (videoAccessState !== MediaAccessState.allowed);
    useEffect(() => {
        if (isPhoneAudio && isAudioEnabled) {
            toggleAudioEnabled();
        }
        if (!isPhoneAudio && !isAudioEnabled) {
            toggleAudioEnabled();
        }
    }, [isPhoneAudio]);
    const onAudioToggle = () => {
        toggleAudioEnabled();
        onSettingsChanged('isAudioEnabled', !isAudioEnabled);
    };
    const onVideoToggle = () => {
        toggleVideoEnabled();
        onSettingsChanged('isVideoEnabled', !isVideoEnabled);
    };
    const buttonStyle = util.mergeStyles(undefined, styles.button, [styles.buttonInverted, !isVideoEnabled]);
    const buttonNextSelectorStyle = util.mergeStyles(undefined, styles.button, [styles.buttonInverted, !isVideoEnabled], styles.buttonNextSelector);
    if (isAudioPreview) {
        return (React.createElement(Stack, { anchor: "bottom" },
            React.createElement(Stack.Layer, null,
                React.createElement(Iffy, { is: !audioException && !isPhoneAudio },
                    React.createElement(DNABox, { appearance: "col", style: styles.audioContainer },
                        React.createElement(DNAText, { style: styles.mainAudioBoxTitle }, "Please ensure your microphone & speakers are working."),
                        React.createElement(DNAText, { style: styles.audioSubtitle }, "SPEAKERS"),
                        React.createElement(DNABox, { onLayout: ({ nativeEvent }) => setAudioLayout(nativeEvent.layout.width), appearance: "row", style: { paddingVertical: 10 } },
                            React.createElement(DeviceSelect, { deviceType: "audiooutput", customStyles: { minWidth: 100, width: audioLayout - 70 }, basicSelectStyle: true }),
                            React.createElement(Popover, { type: "tooltip" },
                                React.createElement(Popover.Anchor, null,
                                    React.createElement(DNABox, { style: buttonNextSelectorStyle },
                                        React.createElement(AudioTestButton, { status: Status.warning, audioFiles: audioFiles !== null && audioFiles !== void 0 ? audioFiles : [] }))),
                                React.createElement(Popover.Content, null,
                                    React.createElement(DNAText, { status: "basic" }, "If you cannot hear the test sound, select a different speaker.")))),
                        React.createElement(DNAText, { style: styles.audioSubtitle }, "MICROPHONE"),
                        React.createElement(DNABox, { appearance: "row", alignX: "start", style: { marginRight: 90 } },
                            React.createElement(DNABox, null,
                                React.createElement(DeviceSelect, { deviceType: "audioinput", customStyles: { minWidth: 100, width: audioLayout - 70 }, basicSelectStyle: true })),
                            React.createElement(DNABox, null,
                                React.createElement(Iffy, { is: !isPhoneAudio },
                                    React.createElement(Popover, { type: "tooltip" },
                                        React.createElement(Popover.Anchor, null,
                                            React.createElement(DNAButton, { style: [buttonNextSelectorStyle, { backgroundColor: colors['color-text-basic'] }], onPress: onAudioToggle },
                                                React.createElement(DNABox, { style: { marginTop: 5 } },
                                                    React.createElement(LocalAudioLevelIndicator, { size: 22, background: colors['color-gray-100'], color: colors['color-gray-100'] })))),
                                        React.createElement(Popover.Content, null,
                                            React.createElement(DNAText, { status: "basic" }, "Please speak into your microphone. If this mic icon does not flash to green, select a different mic.")))))))))));
    }
    else {
        return (React.createElement(Stack, { anchor: "bottom" },
            React.createElement(Stack.Layer, null,
                React.createElement(DNABox, { alignX: "center", appearance: "col", style: { marginLeft: 5 } },
                    React.createElement(DNABox, { appearance: "row", alignX: "center", style: { zIndex: 2 } },
                        React.createElement(DNABox, { style: styles.previewStatusContainer },
                            React.createElement(DNAText, { style: styles.previewText }, "Preview"))),
                    React.createElement(DNABox, { style: { height: 190, width: 340 } },
                        React.createElement(LocalVideoPreview, { customContainerStyle: styles.localVideoPreview, videoAccessState: videoAccessState })),
                    React.createElement(Iffy, { is: !videoException },
                        React.createElement(DNABox, { appearance: "row", alignX: "center", style: { paddingHorizontal: 0, padding: 10, width: 340 } },
                            React.createElement(DeviceSelect, { deviceType: "videoinput", customStyles: { minWidth: 100, width: '100%', paddingRight: 0 }, basicSelectStyle: true }))))),
            React.createElement(Stack.Layer, null,
                React.createElement(DNABox, { appearance: "row", style: { paddingBottom: 5, marginBottom: 75 } },
                    React.createElement(Iffy, { is: !isPhoneAudio && !videoException },
                        React.createElement(Button.Kitten, { style: buttonStyle, onPress: onAudioToggle },
                            React.createElement(DNABox, { style: { marginTop: 5 } },
                                React.createElement(LocalAudioLevelIndicator, { size: 22, background: colors['color-text-basic'] })))),
                    React.createElement(Iffy, { is: !videoException },
                        React.createElement(Button.Kitten, { style: buttonStyle, onPress: onVideoToggle },
                            React.createElement(Icon, { name: isVideoEnabled ? 'video' : 'video-off', style: styles.icon })))))));
    }
}
export default Preview;
//# sourceMappingURL=Preview.js.map